// Typed fetch helpers for /api/v1. The session cookie rides along automatically (same origin).
import type { ApiError } from '@wbwl/contract'

export class ApiFailure extends Error {
  readonly status: number
  readonly body: ApiError

  constructor(status: number, body: ApiError) {
    super(body.message || body.error)
    this.status = status
    this.body = body
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
  })
  if (res.status === 204) return undefined as T
  const text = await res.text()
  let json = null
  try {
    json = text ? JSON.parse(text) : null
  } catch {
    // Not JSON: a proxy's HTML error page, say. Still an ApiFailure, never a SyntaxError.
    throw new ApiFailure(res.status, { error: 'server_error', message: 'The server isn’t answering right now. Try again in a minute.' })
  }
  if (!res.ok) throw new ApiFailure(res.status, json && typeof json.error === 'string' ? json : { error: 'server_error' })
  return json as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string, body: unknown = {}) => request<T>('DELETE', path, body),
}
